import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Target, TrendingUp, Trophy, Calendar } from "lucide-react";

function ymNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmt(n) { return `$${Number(n || 0).toFixed(2)}`; }

function ProgressBar({ pct, tone = "blue" }) {
  const p = pct == null ? 0 : Math.min(100, pct);
  const map = {
    blue: "bg-blue-500",
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
  };
  const superado = pct != null && pct >= 100;
  return (
    <div className="h-3 bg-slate-100 rounded-full overflow-hidden relative">
      <div className={`h-full transition-all ${superado ? "bg-emerald-500" : map[tone]}`} style={{ width: `${p}%` }} />
      {superado && (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white font-mono uppercase tracking-widest pointer-events-none">
          Superada
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, done, target, unit = "", tone = "blue" }) {
  const pct = target ? Math.round((done / target) * 100) : null;
  const remaining = target != null ? Math.max(0, target - done) : null;
  const superada = pct != null && pct >= 100;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className="flex items-baseline justify-between mt-1">
        <div className="font-heading font-extrabold text-3xl text-slate-900">
          {unit}{typeof done === "number" && unit === "$" ? done.toFixed(0) : done}
          {target != null && <span className="text-lg font-normal text-slate-400"> / {unit}{typeof target === "number" && unit === "$" ? target.toFixed(0) : target}</span>}
        </div>
        {pct != null && (
          <div className={`text-lg font-heading font-extrabold font-mono ${superada ? "text-emerald-600" : "text-blue-600"}`}>
            {pct}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <ProgressBar pct={pct} tone={tone} />
      </div>
      <div className="mt-2 text-xs text-slate-500">
        {target == null
          ? "Sin meta configurada"
          : superada
            ? <span className="text-emerald-700 font-semibold">Meta superada · +{unit}{Number(done - target).toFixed(unit === "$" ? 2 : 0)}</span>
            : <span>Falta {unit}{Number(remaining).toFixed(unit === "$" ? 2 : 0)} para la meta</span>}
      </div>
    </div>
  );
}

export default function MyGoals() {
  const [month, setMonth] = useState(ymNow());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/me/production-goals?month=${month}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const currentWeek = data?.weekly?.find((w) => w.is_current) || null;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      <header>
        <h1 className="font-heading font-extrabold text-3xl text-slate-900 flex items-center gap-2">
          <Target size={26} className="text-blue-600" /> Mis metas
        </h1>
        <p className="text-sm text-slate-500 mt-1">Solo se cuenta lo <b>finalizado</b> — el pago o retiro del cliente no afecta tu producción.</p>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Mes</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input-base max-w-[180px]" data-testid="my-goals-month" />
      </div>

      {loading && <div className="text-center text-slate-500 py-8">Cargando…</div>}

      {!loading && data && (
        <>
          {/* SEMANA ACTUAL */}
          {currentWeek ? (
            <section className="space-y-3" data-testid="my-goals-current-week">
              <div className="flex items-baseline gap-2">
                <TrendingUp size={16} className="text-blue-600" />
                <h2 className="font-heading font-extrabold text-lg text-slate-900">Meta de esta semana · Semana {currentWeek.week_index}</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MetricCard label="Órdenes finalizadas" done={currentWeek.orders_done} target={currentWeek.orders_target} />
                <MetricCard label="Producción" done={currentWeek.sales_done} target={currentWeek.sales_target} unit="$" tone="emerald" />
              </div>
            </section>
          ) : (
            <div className="text-slate-500 text-sm rounded-2xl border border-dashed border-slate-200 p-4 text-center">
              No estás dentro del mes en curso, o no hay semana activa.
            </div>
          )}

          {/* MES */}
          <section className="space-y-3" data-testid="my-goals-monthly">
            <div className="flex items-baseline gap-2">
              <Trophy size={16} className="text-amber-600" />
              <h2 className="font-heading font-extrabold text-lg text-slate-900">Meta del mes</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MetricCard label="Órdenes finalizadas (mes)" done={data.monthly.orders_done} target={data.monthly.orders_target} />
              <MetricCard label="Producción del mes" done={data.monthly.sales_done} target={data.monthly.sales_target} unit="$" tone="emerald" />
            </div>
          </section>

          {/* PRODUCCIÓN ACUMULADA */}
          <section className="grid grid-cols-2 gap-3" data-testid="my-goals-summary">
            <div className="card-base p-4">
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Órdenes esta semana</div>
              <div className="font-heading font-extrabold text-2xl text-slate-900">{currentWeek?.orders_done ?? 0}</div>
              <div className="text-xs font-mono text-emerald-700 mt-0.5">{fmt(currentWeek?.sales_done ?? 0)}</div>
            </div>
            <div className="card-base p-4">
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Órdenes este mes</div>
              <div className="font-heading font-extrabold text-2xl text-slate-900">{data.monthly.orders_done}</div>
              <div className="text-xs font-mono text-emerald-700 mt-0.5">{fmt(data.monthly.sales_done)}</div>
            </div>
          </section>

          {/* HISTORIAL SEMANAL */}
          <section data-testid="my-goals-weekly-history">
            <div className="flex items-baseline gap-2 mb-2">
              <Calendar size={16} className="text-slate-500" />
              <h2 className="font-heading font-extrabold text-lg text-slate-900">Semanas del mes</h2>
            </div>
            <div className="space-y-2">
              {data.weekly.map((w) => {
                const opct = w.orders_pct;
                const spct = w.sales_pct;
                const overall = Math.max(opct || 0, spct || 0);
                const superada = overall >= 100 && (opct != null || spct != null);
                return (
                  <div key={w.week_index} className={`card-base p-3 ${w.is_current ? "border-blue-400 border-2" : ""}`} data-testid={`my-goals-week-${w.week_index}`}>
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="font-heading font-bold text-slate-900">
                        Semana {w.week_index} {w.is_current && <span className="ml-2 text-[10px] uppercase font-mono tracking-widest text-blue-600">Actual</span>}
                      </div>
                      <div className="text-xs font-mono text-slate-500">
                        {new Date(w.period_start).toLocaleDateString()} – {new Date(new Date(w.period_end).getTime() - 86400000).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-slate-500 text-xs">Órdenes</div>
                        <div className="font-mono"><b>{w.orders_done}</b>{w.orders_target != null && <span className="text-slate-400"> / {w.orders_target}</span>}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 text-xs">Producción</div>
                        <div className="font-mono"><b>{fmt(w.sales_done)}</b>{w.sales_target != null && <span className="text-slate-400"> / {fmt(w.sales_target)}</span>}</div>
                      </div>
                    </div>
                    {(opct != null || spct != null) && (
                      <div className={`mt-1 text-xs font-mono ${superada ? "text-emerald-700 font-bold" : "text-blue-700"}`}>
                        {Math.round(overall)}% {superada && "· Meta superada"}
                      </div>
                    )}
                    {opct == null && spct == null && (
                      <div className="mt-1 text-xs text-slate-400 italic">Sin meta configurada</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
